import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Sun, Calendar, Briefcase, Heart, Activity, BookOpen, ArrowRight, AlertCircle, Gift } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserReadings } from '../../lib/supabase';
import Card, { CardHeader, CardTitle, CardContent, CardFooter } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { trackEvent } from '../../lib/analytics';

type ReadingType = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'career' | 'love' | 'health';

interface ReadingOption {
  type: ReadingType;
  title: string;
  description: string;
  icon: React.ReactNode;
  bgClass: string;
}

// Framer Motion variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const Dashboard: React.FC = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [recentReadings, setRecentReadings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const readingOptions: ReadingOption[] = [
    {
      type: 'daily',
      title: 'Daily Horoscope',
      description: 'Your celestial forecast for today',
      icon: <Sun size={24} />,
      bgClass: 'from-amber-500 to-orange-400',
    },
    {
      type: 'weekly',
      title: 'Weekly Prediction',
      description: 'Your week ahead in the stars',
      icon: <Calendar size={24} />,
      bgClass: 'from-blue-500 to-cyan-400',
    },
    {
      type: 'monthly',
      title: 'Monthly Forecast',
      description: 'Celestial guidance for the month',
      icon: <BookOpen size={24} />,
      bgClass: 'from-violet-500 to-purple-400',
    },
    {
      type: 'quarterly',
      title: 'Quarterly Forecast',
      description: 'Three-month astrological guidance',
      icon: <Calendar size={24} />,
      bgClass: 'from-green-600 to-teal-400',
    },
    {
      type: 'yearly',
      title: 'Yearly Forecast',
      description: 'Your complete astrological year ahead',
      icon: <Star size={24} />,
      bgClass: 'from-amber-600 to-yellow-400',
    },
    {
      type: 'career',
      title: 'Career Reading',
      description: 'Professional path insights',
      icon: <Briefcase size={24} />,
      bgClass: 'from-green-500 to-emerald-400',
    },
    {
      type: 'love',
      title: 'Love & Relationships',
      description: 'Romantic astrological guidance',
      icon: <Heart size={24} />,
      bgClass: 'from-red-500 to-pink-400',
    },
    {
      type: 'health',
      title: 'Health & Wellness',
      description: 'Celestial health insights',
      icon: <Activity size={24} />,
      bgClass: 'from-teal-500 to-green-400',
    },
  ];

  useEffect(() => {
    const fetchRecentReadings = async () => {
      if (user && user.phoneNumber) {
        try {
          const readings = await getUserReadings(user.phoneNumber);
          setRecentReadings(readings.slice(0, 3));
        } catch (error) {
          console.error('Error fetching readings:', error);
          setError('Failed to load readings');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchRecentReadings();
  }, [user]);

  const handleReadingClick = (option: ReadingOption) => {
    trackEvent('reading_selection', 'click', option.type);
    navigate(`/reading/${option.type}`);
  };

  const handlePremiumClick = () => {
    trackEvent('premium_click', 'click', 'dashboard_banner');
    navigate('/premium');
  };

  const getUserAccessStatus = () => {
    if (!userProfile) return null;
    
    if (userProfile.is_premium) {
      return {
        type: 'premium',
        message: 'Premium Member - Unlimited Readings',
        icon: <Star className="h-5 w-5 text-secondary-500" />,
        bgClass: 'bg-secondary-50 border-secondary-200',
        textClass: 'text-secondary-800'
      };
    }
    
    if (!userProfile.has_used_free_reading) {
      return {
        type: 'free',
        message: 'Free Reading Available! 🎁',
        icon: <Gift className="h-5 w-5 text-green-600" />,
        bgClass: 'bg-green-50 border-green-200',
        textClass: 'text-green-800'
      };
    }
    
    if (userProfile.shares_available && userProfile.shares_available > 0) {
      return {
        type: 'shares',
        message: `${userProfile.shares_available} Reading${userProfile.shares_available === 1 ? '' : 's'} Available`,
        icon: <Star className="h-5 w-5 text-blue-600" />,
        bgClass: 'bg-blue-50 border-blue-200',
        textClass: 'text-blue-800'
      };
    }
    
    return {
      type: 'none',
      message: 'Share on WhatsApp or upgrade to premium for more readings',
      icon: <AlertCircle className="h-5 w-5 text-amber-600" />,
      bgClass: 'bg-amber-50 border-amber-200',
      textClass: 'text-amber-800'
    };
  };

  // Early return with error message if there's an issue
  if (!user) {
    return (
      <div className="min-h-screen bg-neutral-50 p-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-center">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-base font-semibold text-red-800">Authentication Error</h3>
                <p className="text-red-700 text-sm">User not found. Please try logging in again.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const accessStatus = getUserAccessStatus();

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section with Greeting */}
      <div className="bg-cosmic-gradient text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold font-display">
            Namaste{userProfile?.name ? `, ${userProfile.name}` : ''}
          </h1>
          <p className="mt-2 text-secondary-300">
            The stars are aligned for your celestial guidance today
          </p>
          
          {/* Access Status Banner */}
          {accessStatus && (
            <div className={`mt-4 p-3 rounded-lg border ${accessStatus.bgClass}`}>
              <div className="flex items-center">
                {accessStatus.icon}
                <span className={`ml-2 font-medium ${accessStatus.textClass}`}>
                  {accessStatus.message}
                </span>
              </div>
            </div>
          )}
          
          {!userProfile?.is_premium && (
            <Button 
              variant="secondary" 
              className="mt-4"
              rightIcon={<ArrowRight size={16} />}
              onClick={handlePremiumClick}
            >
              Upgrade to Premium
            </Button>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* All Readings Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-4">
            Astrological Readings
          </h2>
          <p className="mb-6 text-neutral-600">
            Choose your reading type to get personalized celestial guidance
            {!userProfile?.has_used_free_reading && !userProfile?.is_premium && 
              <span className="text-green-600 font-medium"> - Your first reading is free! 🎁</span>
            }
          </p>
          
          <motion.div 
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {readingOptions.map((option) => (
              <motion.div key={option.type} variants={item}>
                <div onClick={() => handleReadingClick(option)}>
                  <Card className="h-full transition-transform duration-300 hover:scale-105 cursor-pointer relative overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${option.bgClass} opacity-10`}></div>
                    <CardContent className="relative z-10">
                      <div className="flex items-start">
                        <div className={`p-3 rounded-lg bg-gradient-to-br ${option.bgClass} text-white`}>
                          {option.icon}
                        </div>
                        
                        <div className="ml-4">
                          <h3 className="font-semibold text-lg">{option.title}</h3>
                          <p className="text-neutral-600 text-sm">{option.description}</p>
                          {!userProfile?.has_used_free_reading && !userProfile?.is_premium && (
                            <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                              FREE
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
        
        {/* Recent Readings */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Recent Readings</h2>
            <Link 
              to="/readings" 
              className="text-primary-600 hover:text-primary-700 text-sm font-semibold"
              onClick={() => trackEvent('navigation', 'click', 'view_all_readings')}
            >
              View All
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p>Loading your readings...</p>
              </div>
            </div>
          ) : error ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-4">
                <div className="flex items-center">
                  <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                  <div>
                    <h3 className="text-base font-semibold text-red-800">Error Loading Readings</h3>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : recentReadings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentReadings.map((reading) => (
                <Card key={reading.reading_id}>
                  <CardHeader>
                    <CardTitle className="capitalize">
                      {reading.reading_type} Reading
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-neutral-600 line-clamp-3">
                      {reading.prediction_text.substring(0, 150)}...
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        trackEvent('reading_result', 'click', reading.reading_type);
                        navigate(`/reading/result/${reading.reading_id}`);
                      }}
                    >
                      Read Full Prediction
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <Star className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Readings Yet</h3>
                <p className="text-neutral-600 mb-4">
                  Get your first astrological reading to see your celestial predictions.
                  {!userProfile?.has_used_free_reading && !userProfile?.is_premium && (
                    <span className="block text-green-600 font-medium mt-1">
                      🎁 Your first reading is completely free!
                    </span>
                  )}
                </p>
                <Button onClick={() => {
                  trackEvent('first_reading', 'click', 'daily');
                  navigate('/reading/daily');
                }}>
                  {!userProfile?.has_used_free_reading && !userProfile?.is_premium ? 
                    'Get Your Free Reading' : 'Get Your First Reading'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;